import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface LegalDeadline {
  id: string;
  name: string;
  legalArea: string;
  deadlineType: string | null;
  daysCount: number | null;
  countType: 'business_days' | 'calendar_days' | 'variable';
  legalBasis: string | null;
  startTerm: string | null;
  endTerm: string | null;
  practicalNotes: string | null;
  riskLevel: 'low' | 'medium' | 'high';
  tags: string;
  isPinned: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLegalDeadlineInput {
  name: string;
  legalArea: string;
  deadlineType?: string | null;
  daysCount?: number | null;
  countType: 'business_days' | 'calendar_days' | 'variable';
  legalBasis?: string | null;
  startTerm?: string | null;
  endTerm?: string | null;
  practicalNotes?: string | null;
  riskLevel: 'low' | 'medium' | 'high';
  tags?: string | null;
  isPinned?: boolean;
  isActive?: boolean;
}

export type UpdateLegalDeadlineInput = CreateLegalDeadlineInput;

type UseLegalDeadlinesOptions = {
  autoLoad?: boolean;
};

type LegalDeadlinesApiResponse = {
  success?: boolean;
  legalDeadlines?: unknown;
  legalDeadline?: unknown;
  error?: string;
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toBoolean = (value: unknown, fallback = false) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['0', 'false', 'inactive', 'inativo', 'archived', 'arquivado', 'nao', 'não'].includes(normalized)) {
    return false;
  }

  return true;
};

const toDaysCount = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const numeric = Number(String(value).replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeCountType = (value: unknown): LegalDeadline['countType'] => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'business_days' || normalized === 'calendar_days' || normalized === 'variable') {
    return normalized;
  }
  return 'variable';
};

const normalizeRiskLevel = (value: unknown): LegalDeadline['riskLevel'] => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'low' || normalized === 'medium' || normalized === 'high') {
    return normalized;
  }
  return 'medium';
};

const normalizeDeadline = (value: any): LegalDeadline => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? '').trim(),
  legalArea: String(value?.legalArea ?? value?.legal_area ?? '').trim(),
  deadlineType: toNullableText(value?.deadlineType ?? value?.deadline_type),
  daysCount: toDaysCount(value?.daysCount ?? value?.days_count),
  countType: normalizeCountType(value?.countType ?? value?.count_type),
  legalBasis: toNullableText(value?.legalBasis ?? value?.legal_basis),
  startTerm: toNullableText(value?.startTerm ?? value?.start_term),
  endTerm: toNullableText(value?.endTerm ?? value?.end_term),
  practicalNotes: toNullableText(value?.practicalNotes ?? value?.practical_notes),
  riskLevel: normalizeRiskLevel(value?.riskLevel ?? value?.risk_level),
  tags: String(value?.tags ?? '').trim(),
  isPinned: toBoolean(value?.isPinned ?? value?.is_pinned),
  isActive: toBoolean(value?.isActive ?? value?.is_active, true),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? new Date().toISOString(),
});

const sortLegalDeadlines = (a: LegalDeadline, b: LegalDeadline) =>
  Number(b.isPinned) - Number(a.isPinned) ||
  Number(b.isActive) - Number(a.isActive) ||
  a.legalArea.localeCompare(b.legalArea, 'pt-BR') ||
  a.name.localeCompare(b.name, 'pt-BR');

const mergeAndSortLegalDeadlines = (
  setLegalDeadlines: Dispatch<SetStateAction<LegalDeadline[]>>,
  next: LegalDeadline[]
) => {
  setLegalDeadlines([...next].sort(sortLegalDeadlines));
};

export function useLegalDeadlines(options: UseLegalDeadlinesOptions = {}) {
  const { autoLoad = true } = options;
  const [legalDeadlines, setLegalDeadlines] = useState<LegalDeadline[]>([]);
  const [isLoadingLegalDeadlines, setIsLoadingLegalDeadlines] = useState(true);
  const [isSavingLegalDeadlines, setIsSavingLegalDeadlines] = useState(false);
  const [legalDeadlinesError, setLegalDeadlinesError] = useState<string | null>(null);
  const [legalDeadlinesSuccess, setLegalDeadlinesSuccess] = useState<string | null>(null);

  const loadLegalDeadlines = useCallback(async (params?: { includeArchived?: boolean }) => {
    setIsLoadingLegalDeadlines(true);
    setLegalDeadlinesError(null);

    try {
      const searchParams = new URLSearchParams();
      if (params?.includeArchived) {
        searchParams.set('includeArchived', '1');
      }

      const query = searchParams.toString();
      const response = await fetch(`/api/legal-deadlines${query ? `?${query}` : ''}`);
      const data = (await response.json()) as LegalDeadlinesApiResponse;

      if (!response.ok || !data.success || !Array.isArray(data.legalDeadlines)) {
        throw new Error(data.error || `GET /api/legal-deadlines falhou com status ${response.status}`);
      }

      mergeAndSortLegalDeadlines(setLegalDeadlines, data.legalDeadlines.map(normalizeDeadline));
    } catch (error) {
      console.error('Erro ao carregar prazos jurídicos.', error);
      setLegalDeadlines([]);
      setLegalDeadlinesError('Não foi possível carregar os prazos jurídicos.');
    } finally {
      setIsLoadingLegalDeadlines(false);
    }
  }, []);

  const createLegalDeadline = useCallback(async (input: CreateLegalDeadlineInput) => {
    setIsSavingLegalDeadlines(true);
    setLegalDeadlinesError(null);
    setLegalDeadlinesSuccess(null);

    try {
      const response = await fetchWithRetry('/api/legal-deadlines', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const data = (await response.json()) as LegalDeadlinesApiResponse;

      if (!response.ok || !data.success || !data.legalDeadline) {
        throw new Error(data.error || `POST /api/legal-deadlines falhou com status ${response.status}`);
      }

      const created = normalizeDeadline(data.legalDeadline);
      setLegalDeadlines((prev) => [...prev.filter((item) => item.id !== created.id), created].sort(sortLegalDeadlines));
      setLegalDeadlinesSuccess('Prazo jurídico criado com sucesso.');
      return created;
    } catch (error: any) {
      const message = error?.message || 'Não foi possível criar o prazo jurídico.';
      setLegalDeadlinesError(message);
      throw error;
    } finally {
      setIsSavingLegalDeadlines(false);
    }
  }, []);

  const updateLegalDeadline = useCallback(async (id: string, input: UpdateLegalDeadlineInput) => {
    setIsSavingLegalDeadlines(true);
    setLegalDeadlinesError(null);
    setLegalDeadlinesSuccess(null);

    try {
      const response = await fetch(`/api/legal-deadlines/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const data = (await response.json()) as LegalDeadlinesApiResponse;

      if (!response.ok || !data.success || !data.legalDeadline) {
        throw new Error(data.error || `PUT /api/legal-deadlines/${id} falhou com status ${response.status}`);
      }

      const updated = normalizeDeadline(data.legalDeadline);
      setLegalDeadlines((prev) => [...prev.filter((item) => item.id !== updated.id), updated].sort(sortLegalDeadlines));
      setLegalDeadlinesSuccess('Prazo jurídico atualizado com sucesso.');
      return updated;
    } catch (error: any) {
      const message = error?.message || 'Não foi possível atualizar o prazo jurídico.';
      setLegalDeadlinesError(message);
      throw error;
    } finally {
      setIsSavingLegalDeadlines(false);
    }
  }, []);

  const archiveLegalDeadline = useCallback(async (id: string) => {
    setIsSavingLegalDeadlines(true);
    setLegalDeadlinesError(null);
    setLegalDeadlinesSuccess(null);

    try {
      const response = await fetch(`/api/legal-deadlines/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });

      const data = (await response.json()) as LegalDeadlinesApiResponse;

      if (!response.ok || !data.success) {
        throw new Error(data.error || `DELETE /api/legal-deadlines/${id} falhou com status ${response.status}`);
      }

      setLegalDeadlines((prev) => prev.filter((item) => item.id !== id));
      setLegalDeadlinesSuccess('Prazo jurídico arquivado com sucesso.');
    } catch (error: any) {
      const message = error?.message || 'Não foi possível arquivar o prazo jurídico.';
      setLegalDeadlinesError(message);
      throw error;
    } finally {
      setIsSavingLegalDeadlines(false);
    }
  }, []);

  const refreshLegalDeadlines = useCallback(async () => {
    await loadLegalDeadlines();
  }, [loadLegalDeadlines]);

  const clearLegalDeadlinesFeedback = useCallback(() => {
    setLegalDeadlinesError(null);
    setLegalDeadlinesSuccess(null);
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      setIsLoadingLegalDeadlines(false);
      return;
    }

    void loadLegalDeadlines();
  }, [autoLoad, loadLegalDeadlines]);

  return {
    legalDeadlines,
    isLoadingLegalDeadlines,
    isSavingLegalDeadlines,
    legalDeadlinesError,
    legalDeadlinesSuccess,
    loadLegalDeadlines,
    createLegalDeadline,
    updateLegalDeadline,
    archiveLegalDeadline,
    refreshLegalDeadlines,
    clearLegalDeadlinesFeedback,
    setLegalDeadlines,
  };
}
